/**
 * LINE LIFF (Front-end Framework) 初期化サービス。
 *
 * nawabari は LINE Mini App / Capacitor (Android/iOS) の複数プラットフォームで動作する。
 * このモジュールは:
 *
 *   1. main.tsx から `initializeLiff()` を呼び、LIFF SDK を初期化する
 *   2. Capacitor ネイティブはスキップ（ネイティブ認証フローを使う）
 *   3. LINE Client 内 (`liff.isInClient() === true`) のみ line_mini として動作
 *   4. sdk.ts の buildGetToken() が `getLiffIdToken()` を呼んで ID Token を取得
 *
 * 設計方針（Match3 相当）:
 *   - 非 Capacitor / 非 localhost では LINE 認証を必須とする。Guest フォールバックしない。
 *   - LIFF 初期化失敗や LINE Client 外アクセスの場合は LINE MINI App URL / liff.login() で
 *     正規ルート（LINE Mini App）にリダイレクトする。
 *   - localhost では VITE_DEV_AUTH_BYPASS=true と組み合わせて開発継続を許可。
 *   - LIFF SDK のトークンキャッシュが古いと init が失敗するため、prepareLiffAuth で事前クリア。
 *
 * 参考: `../../luckyanimal-match3-lineapp/src/services/liff.ts`
 */

import { isCapacitorNative } from '../utils/platform'

// ---------------------------------------------------------------------------
// 型定義（window.liff / window.YmgSDK は CDN 版のためここで最小限のグローバル型を宣言）
// ---------------------------------------------------------------------------

interface LiffInitOptions {
  liffId: string
  withLoginOnExternalBrowser?: boolean
}

interface LiffGlobal {
  init: (options: LiffInitOptions) => Promise<void>
  ready: Promise<void>
  isLoggedIn: () => boolean
  login: (options?: { redirectUri?: string }) => void
  logout: () => void
  getIDToken: () => string | null
  getAccessToken: () => string | null
  isInClient: () => boolean
  isApiAvailable: (api: string) => boolean
  closeWindow: () => void
  getLanguage?: () => string
  shareTargetPicker?: (
    messages: ShareMessage[],
    options?: { isMultiple?: boolean },
  ) => Promise<{ status?: string } | void>
  createShortcutOnHomeScreen?: (params: { url: string }) => Promise<void>
}

declare global {
  interface Window {
    liff?: LiffGlobal
    YmgSDK?: {
      UnifiedSdk?: {
        prepareLiffAuth?: (liffId: string) => { cleared?: boolean; reason?: string }
        clearLiffCache?: (liffId: string) => void
      }
    }
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type LiffInitStatus =
  | 'ready' // LINE Client 内でログイン済み → line_mini として動作可能
  | 'redirecting' // liff.login() または VITE_LIFF_URL へのリダイレクトが進行中
  | 'skipped' // Capacitor 等で LIFF 自体を使わない
  | 'unavailable' // SDK 未ロード / LIFF ID 未設定 / init 失敗（localhost 開発時のみ許容）

export interface LiffInitResult {
  status: LiffInitStatus
  error?: string
}

let cachedResult: LiffInitResult | null = null

/**
 * LIFF が ID Token を提供できる状態にあるかどうか（= init 完了 + ログイン済み）。
 * LINE Client 内外を問わず、外部ブラウザで LIFF ログイン済みなら true になる。
 * Match3 と同じく `isInClient()` は判定に使わない（外部ブラウザ LIFF も line_mini として扱う）。
 */
export function isLiffReady(): boolean {
  return cachedResult?.status === 'ready'
}

/**
 * 現在の LIFF ID Token を返す。LINE Client 内でログイン済みのときのみ値を返す。
 * 未ログイン / LIFF 未初期化 / SDK 未ロードの場合は null。
 * sdk.ts の buildGetToken() で line_mini 時に呼ばれる。
 *
 * LIFF SDK v2 の getIDToken() は同期関数で throw せず、未ログインなら null を返す仕様。
 * そのためここで try/catch は行わない。万が一例外が出たときは上位に伝播させる。
 */
export function getLiffIdToken(): string | null {
  if (!isLiffReady()) return null
  const token = window.liff?.getIDToken?.() ?? null
  if (!token) return null
  if (!isJwtUsable(token)) {
    clearLiffSdkCache()
    return null
  }
  return token
}

/**
 * localhost / 127.0.0.1 上で動いている開発環境かどうか。
 * sdk.ts が VITE_DEV_AUTH_BYPASS と組み合わせてゲスト認証を許可する条件に使う。
 */
export function isLocalhostDev(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1'
}

/**
 * liff.login() に渡す安全な redirectUri を返す。
 *
 * `window.location.href` をそのまま渡すと、攻撃者制御のクエリ文字列や
 * フラグメント（例: `?evil=... #access_token=...`）が LINE 認証後のリダイレクト
 * に引き継がれ、Open Redirect / トークン窃取の足場になりうる
 * （Red Team レビュー HIGH 指摘）。クエリ・フラグメントを除去して
 * origin + pathname だけに絞り込む。
 */
export function getSafeLiffRedirectUri(): string {
  if (typeof window === 'undefined') return ''
  return window.location.origin + window.location.pathname
}

/**
 * 設定された LINE MINI App URL にリダイレクトする。
 * Match3 の `redirectToLiffMiniApp()` 相当。
 *
 * LIFF 初期化が失敗した場合（誤デプロイ / CDN 障害 / 非 LINE 経由アクセス等）、
 * ユーザーを正規の `https://miniapp.line.me/<LIFF_ID>` に誘導して LINE ログイン経由で
 * 戻って来てもらうための導線。URL を解決できなければ false を返す。
 */
function redirectToLiffMiniApp(): boolean {
  const url = getLiffMiniAppUrl()
  if (!url) {
    console.warn('[liff] LINE MINI App URL is not configured; cannot redirect')
    return false
  }
  console.log(`[liff] Redirecting to LINE Mini App URL: ${url}`)
  window.location.href = url
  return true
}

function getConfiguredLiffId(): string | null {
  return (import.meta.env.VITE_LIFF_ID as string | undefined)?.trim() || null
}

/**
 * LINE MINI App の正規 URL を返す。
 *
 * 公式仕様では 2023-12-13 以降 `https://miniapp.line.me/{liffId}` が
 * LINE MINI App URL。既存 `https://liff.line.me/{liffId}` も動作するが、
 * 共有・審査導線では miniapp ドメインに正規化する。
 */
export function getLiffMiniAppUrl(): string | null {
  const configured = (import.meta.env.VITE_LIFF_URL as string | undefined)?.trim()
  if (configured) return normalizeMiniAppUrl(configured)

  const liffId = getConfiguredLiffId()
  if (!liffId) return null
  return `https://miniapp.line.me/${encodeURIComponent(liffId)}`
}

function normalizeMiniAppUrl(url: string): string {
  return url.replace(/^https:\/\/liff\.line\.me\//, 'https://miniapp.line.me/')
}

function getEndpointUrl(): URL | null {
  const hosting = (import.meta.env.VITE_HOSTING_URL as string | undefined)?.trim()
  try {
    if (hosting) return new URL(hosting)
    if (typeof window !== 'undefined') return new URL(window.location.origin)
  } catch {
    return null
  }
  return null
}

/**
 * Endpoint URL 上のページ URL を LINE MINI App permanent link に変換する。
 *
 * 公式ドキュメントの式:
 *   LIFF URL + (LINE MINI App URL - Endpoint URL) = Permanent Link
 *
 * Flex Message / text share では Firebase Hosting の URL ではなく、この permanent link を渡す。
 */
export function createLineMiniAppPermanentLink(inputUrl?: string): string | null {
  if (typeof window === 'undefined' && !inputUrl) return null

  const miniAppUrl = getLiffMiniAppUrl()
  if (!miniAppUrl) return null

  let source: URL
  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.href : miniAppUrl
    source = new URL(inputUrl ?? baseUrl, baseUrl)
  } catch {
    return miniAppUrl
  }

  const base = normalizeMiniAppUrl(miniAppUrl).replace(/\/$/, '')
  const liffId = getConfiguredLiffId()

  if (source.hostname === 'miniapp.line.me' || source.hostname === 'liff.line.me') {
    const prefix = liffId ? `/${liffId}` : ''
    const suffix = prefix && source.pathname.startsWith(prefix)
      ? source.pathname.slice(prefix.length)
      : ''
    return `${base}${suffix}${source.search}${source.hash}`
  }

  const endpoint = getEndpointUrl()
  if (endpoint && source.origin === endpoint.origin) {
    return `${base}${source.pathname}${source.search}${source.hash}`
  }

  return miniAppUrl
}

/**
 * ホーム画面ショートカット作成 API を表示してよいかを返す。
 * Sweeper と同じく、既にホーム画面起動中の PWA と LIFF API 非対応環境では非表示にする。
 */
export function canAddToHomeScreen(): boolean {
  if (typeof window === 'undefined') return false
  if (isStandaloneMode()) return false

  try {
    return window.liff?.isApiAvailable?.('createShortcutOnHomeScreen') === true
  } catch (err) {
    console.warn('[liff] createShortcutOnHomeScreen availability check failed:', err)
    return false
  }
}

export async function addToHomeScreen(): Promise<boolean> {
  if (!canAddToHomeScreen()) return false

  const shortcutUrl = getLiffMiniAppUrl()
  if (!shortcutUrl) {
    console.warn('[liff] LINE MINI App URL is not configured; cannot create shortcut')
    return false
  }

  try {
    await window.liff?.createShortcutOnHomeScreen?.({ url: shortcutUrl })
    return true
  } catch (err) {
    console.warn('[liff] createShortcutOnHomeScreen failed:', err)
    return false
  }
}

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  if (window.matchMedia?.('(display-mode: fullscreen)').matches) return true
  return 'standalone' in window.navigator
    && (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}

function clearLiffSdkCache(): void {
  const liffId = getConfiguredLiffId()
  if (!liffId) return
  try {
    window.YmgSDK?.UnifiedSdk?.clearLiffCache?.(liffId)
  } catch (err) {
    // fallback:ok — キャッシュクリア失敗時も、期限切れ token を返さないことが主目的。
    console.warn('[liff] clearLiffCache failed:', err)
  }
}

function isJwtUsable(token: string): boolean {
  try {
    const [, payload] = token.split('.')
    if (!payload) return true
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const decoded = JSON.parse(window.atob(padded)) as { exp?: unknown }
    if (typeof decoded.exp !== 'number') return true
    const nowSeconds = Math.floor(Date.now() / 1000)
    return decoded.exp > nowSeconds + 30
  } catch {
    return true
  }
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

function isLocalhost(): boolean {
  return isLocalhostDev()
}

/**
 * LIFF SDK を初期化する。main.tsx から React 描画前に一度だけ呼ぶ。
 *
 * 戻り値の status で呼び出し側は以下を判断できる:
 *   - 'ready'          → LINE Mini App 内でログイン済み
 *   - 'redirecting'    → liff.login() or VITE_LIFF_URL へのリダイレクト発生。
 *                        以降のコードに到達しない想定
 *   - 'skipped'        → Capacitor ネイティブ
 *   - 'unavailable'    → SDK 未ロード / LIFF ID 未設定 / init 失敗。
 *                        localhost 開発時のみ許容され、本番ではエラー表示へ進む
 *
 * 設計方針（Match3 相当）:
 *   - 非 Capacitor / 非 localhost なら、LINE 認証を経由しない限り ready にしない。
 *   - LIFF 初期化が失敗した場合や LINE Client 外にいる場合は、VITE_LIFF_URL
 *     または liff.login() でリダイレクトする。Guest フォールバックは行わない。
 *   - localhost では VITE_DEV_AUTH_BYPASS=true と組み合わせてゲストで継続可能。
 */
export async function initializeLiff(): Promise<LiffInitResult> {
  if (cachedResult) return cachedResult

  // Capacitor ネイティブでは LIFF を触らない
  if (isCapacitorNative()) {
    return (cachedResult = { status: 'skipped' })
  }

  const onLocalhost = isLocalhost()

  if (typeof window === 'undefined' || !window.liff) {
    // SDK が読み込まれていない。localhost なら開発体験のため unavailable で継続。
    // 本番デプロイで未ロードなら誤デプロイ（index.html の <script> 消失等）。
    // LIFF URL にリダイレクトして正規ルートに戻す。
    const error = 'LIFF SDK script not loaded (window.liff is undefined)'
    if (!onLocalhost && redirectToLiffMiniApp()) {
      return (cachedResult = { status: 'redirecting', error })
    }
    return (cachedResult = { status: 'unavailable', error })
  }

  const liffId = getConfiguredLiffId()
  if (!liffId) {
    // LIFF ID 未設定。localhost では開発体験のため継続、本番では異常として扱う。
    return (cachedResult = {
      status: 'unavailable',
      error: 'VITE_LIFF_ID is not configured',
    })
  }

  // YMG SDK が期限切れ LIFF トークンを持っている場合、事前にクリアしないと
  // liff.init() 時に "token expired" で失敗することがある（samegame 実績）。
  // prepareLiffAuth は補助的なクリーンアップなので、失敗しても init は試みる。
  try {
    const prepare = window.YmgSDK?.UnifiedSdk?.prepareLiffAuth
    if (typeof prepare === 'function') {
      const result = prepare(liffId)
      if (result?.cleared) {
        console.log(`[liff] prepareLiffAuth cleared stale token (${result.reason ?? 'unknown'})`)
      }
    }
  } catch (err) {
    // fallback:ok — prepareLiffAuth は init 前の予防措置。失敗してもliff.init()を試す価値がある
    console.warn('[liff] prepareLiffAuth failed (continuing to liff.init):', err)
  }

  try {
    await window.liff.init({ liffId })
  } catch (err) {
    // LIFF init 失敗: 非 localhost では VITE_LIFF_URL へリダイレクトして正規ルートに戻す。
    // localhost では開発継続（unavailable）。
    const message = err instanceof Error ? err.message : String(err)
    console.warn(`[liff] init failed: ${message}`)
    if (!onLocalhost && redirectToLiffMiniApp()) {
      return (cachedResult = { status: 'redirecting', error: message })
    }
    return (cachedResult = { status: 'unavailable', error: message })
  }

  // LIFF ログイン判定（Match3 と同じ。LINE Client 内外は問わない）。
  // 外部ブラウザでも LIFF ログイン済みなら getIDToken() で ID Token を取得できる仕様。
  if (!window.liff.isLoggedIn()) {
    // localhost 開発では login() で LIFF 認証ループに入らないよう unavailable で継続。
    if (onLocalhost) {
      console.log('[liff] not logged in (localhost). Continuing for dev.')
      return (cachedResult = { status: 'unavailable', error: 'not-logged-in (localhost)' })
    }
    console.log('[liff] not logged in. Redirecting to LINE login...')
    // redirectUri は origin + pathname のみに絞る（Open Redirect 対策）。
    window.liff.login({ redirectUri: getSafeLiffRedirectUri() })
    return (cachedResult = { status: 'redirecting' })
  }

  // ID Token が取得できるかを先に確認する（openid scope 不足で null が返るケースを検出）。
  const idToken = window.liff.getIDToken()
  if (!idToken) {
    // openid scope が LIFF Channel に設定されていない等の構成問題。
    // ゲストに落とすとユーザーが分離されるため、unavailable として明示的に失敗させる。
    const error = 'LIFF getIDToken() returned null (check openid scope on LIFF channel)'
    console.warn(`[liff] ${error}`)
    return (cachedResult = { status: 'unavailable', error })
  }

  // LINE プロフィール（getProfile）は起動経路で呼ばない。
  // チャネル同意の簡略化により初回ユーザーは openid のみで開くため、getProfile() は
  // アクセス許可要求サブウィンドウを起動時に出してしまう。表示名・画像は YMG Hub の
  // プロフィール（getMyProfile）が正で、LINE プロフィールは不要。
  console.log(`[liff] ready (inClient=${window.liff.isInClient()})`)
  return (cachedResult = { status: 'ready' })
}
// ---------------------------------------------------------------------------
// Flex Message types (LINE Messaging API)
// ---------------------------------------------------------------------------
// 招待用 Flex Message を組み立てるための型定義。Sweeper 実装準拠。
// LIFF SDK 自体は型を配布していないため、ここで最小限に宣言する。

export interface ShareMessage {
  type: 'text' | 'image' | 'video' | 'audio' | 'location' | 'sticker' | 'template' | 'flex'
  text?: string
  originalContentUrl?: string
  previewImageUrl?: string
  altText?: string
  contents?: FlexContainer
}

export interface FlexContainer {
  type: 'bubble' | 'carousel'
  hero?: FlexImage
  body?: FlexBox
  footer?: FlexBox
  styles?: {
    hero?: { backgroundColor?: string }
    body?: { backgroundColor?: string }
    footer?: { backgroundColor?: string }
  }
}

export interface FlexBox {
  type: 'box'
  layout: 'horizontal' | 'vertical' | 'baseline'
  contents: FlexComponent[]
  spacing?: string
  margin?: string
  paddingAll?: string
  paddingTop?: string
  paddingBottom?: string
  backgroundColor?: string
  justifyContent?:
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
  alignItems?: 'flex-start' | 'center' | 'flex-end'
  action?: FlexAction
}

export interface FlexImage {
  type: 'image'
  url: string
  size?: string
  aspectRatio?: string
  aspectMode?: 'cover' | 'fit'
  action?: FlexAction
}

export interface FlexText {
  type: 'text'
  text: string
  size?: string
  weight?: 'regular' | 'bold'
  color?: string
  align?: 'start' | 'center' | 'end'
  wrap?: boolean
  margin?: string
  flex?: number
  gravity?: 'top' | 'center' | 'bottom'
}

export interface FlexButton {
  type: 'button'
  action: FlexAction
  style?: 'primary' | 'secondary' | 'link'
  color?: string
  height?: 'sm' | 'md'
}

export interface FlexAction {
  type: 'uri' | 'message' | 'postback'
  label?: string
  uri?: string
  text?: string
  data?: string
}

export interface FlexSeparator {
  type: 'separator'
  margin?: string
  color?: string
}

export type FlexComponent = FlexBox | FlexImage | FlexText | FlexButton | FlexSeparator

// ---------------------------------------------------------------------------
// Share 用アセット URL ビルダー（Firebase Hosting 上の公開アセットを指す）
// ---------------------------------------------------------------------------
// Flex Message 内の画像は LINE 側からアクセス可能な HTTPS URL であることが必須。
// `public/banner_1200_630.webp` 等は別タスクで配置される。

const DEFAULT_HOSTING_URL = 'https://luckyanimal-nawabari.web.app'

function resolveHostingUrl(): string {
  const hosting = (import.meta.env.VITE_HOSTING_URL as string | undefined)?.trim()
  if (hosting) return hosting
  return DEFAULT_HOSTING_URL
}

function getGameBannerUrl(): string {
  const override = (import.meta.env.VITE_GAME_BANNER_URL as string | undefined)?.trim()
  if (override) return override
  return `${resolveHostingUrl()}/banner_1200_630.webp`
}

function getGameIconUrl(): string {
  return `${resolveHostingUrl()}/icon.png`
}

function getMiniAppArrowUrl(): string {
  return `${resolveHostingUrl()}/miniapp_arrow.svg`
}

// ---------------------------------------------------------------------------
// ロケール別シェアテキスト
// ---------------------------------------------------------------------------

interface ShareContent {
  gameName: string
  gameTitle: string
  inviteAltText: string
  gameDescription: string
  playButton: string
}

/** 言語に基づいた招待用テキストを返す。ja* は日本語、それ以外は英語。 */
export function getShareContent(locale: string = 'ja_JP'): ShareContent {
  if (locale.startsWith('ja')) {
    return {
      gameName: 'ラッキーどうぶつナワバリ',
      gameTitle: '⚫ ラッキーどうぶつナワバリ',
      inviteAltText: '⚫ ラッキーどうぶつナワバリに招待されました！',
      gameDescription:
        '5連で即勝利。連鎖スコアでナワバリ争奪。戦略が問われる、引き分けなしの五目並べ！',
      playButton: '遊んでみる',
    }
  }
  return {
    gameName: 'Lucky Animal Nawabari',
    gameTitle: '⚫ Lucky Animal Nawabari',
    inviteAltText: "You've been invited to Lucky Animal Nawabari!",
    gameDescription:
      '5-in-a-row wins instantly. Chain scores for territory control. Strategic gomoku, no draws!',
    playButton: 'Play now',
  }
}

// ---------------------------------------------------------------------------
// Flex Message 構築
// ---------------------------------------------------------------------------
// LINE Mini App の審査要件により、Flex Message のフッターには以下を含めること:
//   - ミニアプリアイコン
//   - ミニアプリ名（クリック可能リンク）
//   - ナビゲーション矢印アイコン

function createFlexFooter(button: FlexButton, appUrl: string, locale: string): FlexBox {
  const iconUrl = getGameIconUrl()
  const arrowUrl = getMiniAppArrowUrl()
  const content = getShareContent(locale)

  return {
    type: 'box',
    layout: 'vertical',
    contents: [
      button,
      {
        type: 'separator',
        margin: 'md',
      } as FlexSeparator,
      {
        type: 'box',
        layout: 'horizontal',
        contents: [
          {
            type: 'image',
            url: iconUrl,
            size: '20px',
            aspectMode: 'cover',
            aspectRatio: '1:1',
          } as FlexImage,
          {
            type: 'text',
            text: content.gameName,
            size: 'xs',
            color: '#999999',
            flex: 1,
            margin: 'sm',
            gravity: 'center',
          } as FlexText,
          {
            type: 'image',
            url: arrowUrl,
            size: '12px',
            aspectMode: 'cover',
            aspectRatio: '1:1',
          } as FlexImage,
        ],
        alignItems: 'center',
        paddingAll: 'sm',
        margin: 'md',
        action: {
          type: 'uri',
          uri: appUrl,
        },
      } as FlexBox,
    ],
    paddingAll: 'lg',
    backgroundColor: '#F8F8F8',
  }
}

/**
 * 招待用 Flex Message バブルを構築する。
 *
 * @param inviteUrl ゲスト招待コードを含む URL
 * @param locale    ja_JP / en_US など。liff.getLanguage() 由来を想定
 */
export function createInviteFlexMessage(
  inviteUrl: string,
  locale: string = 'ja_JP',
): ShareMessage {
  const bannerUrl = getGameBannerUrl()
  const content = getShareContent(locale)
  const liffUrl = getLiffMiniAppUrl() || inviteUrl

  return {
    type: 'flex',
    altText: content.inviteAltText,
    contents: {
      type: 'bubble',
      hero: {
        type: 'image',
        url: bannerUrl,
        size: 'full',
        aspectRatio: '1200:630',
        aspectMode: 'cover',
        action: {
          type: 'uri',
          uri: inviteUrl,
        },
      },
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'text',
            text: content.gameTitle,
            weight: 'bold',
            size: 'lg',
            color: '#1DB446',
            align: 'center',
          } as FlexText,
          {
            type: 'text',
            text: content.gameDescription,
            size: 'sm',
            color: '#666666',
            align: 'center',
            margin: 'md',
            wrap: true,
          } as FlexText,
        ],
        paddingAll: 'lg',
        backgroundColor: '#FFFFFF',
      },
      footer: createFlexFooter(
        {
          type: 'button',
          action: { type: 'uri', label: content.playButton, uri: inviteUrl },
          style: 'primary',
          color: '#06C755',
          height: 'md',
        },
        liffUrl,
        locale,
      ),
    },
  }
}

/**
 * Flex Message を shareTargetPicker 経由で共有する。
 *
 * 戻り値:
 *   - true: 共有成功（LINE が status: 'success' を返したか、古い SDK で void を返した場合）
 *   - false: shareTargetPicker 非対応 / Flex 不受理 / 送信失敗
 *
 * 呼び出し側（social.ts の tryLiffShare）は false を受け取ったらプレーンテキストの
 * shareTargetPicker に切り替える設計。false はエラー隠蔽ではなく「この経路では送れなかった」
 * ことを下位フォールバックへ明示的に委譲するセンチネル。
 */
export async function shareWithFlexMessage(flexMessage: ShareMessage): Promise<boolean> {
  if (typeof window === 'undefined' || !window.liff) return false
  if (!window.liff.isApiAvailable?.('shareTargetPicker')) {
    console.warn('[liff] shareTargetPicker is not available')
    return false
  }
  if (!window.liff.shareTargetPicker) return false

  try {
    const result = await window.liff.shareTargetPicker([flexMessage])
    if (result && typeof result === 'object' && 'status' in result) {
      return (result as { status?: string }).status === 'success'
    }
    // 古い LIFF SDK は成功時に void を返す
    return true
  } catch (err) {
    // fallback:ok — Flex が LINE 側で不受理なら、呼び出し元が text にフォールバック。
    // ここで throw すると text フォールバックに到達しないため、明示的 false を返す。
    console.error('[liff] Flex Message share failed:', err)
    return false
  }
}
